import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { mangaList, categories, categorySlugMap } from '@/mocks/manga';
import type { MangaItem } from '@/mocks/manga';
import Breadcrumb from '@/components/feature/Breadcrumb';

function MangaCard({ manga }: { manga: MangaItem }) {
  return (
    <Link
      to={`/manga/${manga.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={manga.coverImage}
          alt={manga.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
            manga.status === '连载中'
              ? 'bg-primary-500 text-background-50'
              : 'bg-foreground-600 text-background-50'
          }`}>
            {manga.status}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <div className="flex items-center gap-1 text-background-50 text-xs">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-star-fill text-secondary-500"></i>
            </span>
            {manga.rating}
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 truncate font-heading">
          {manga.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 truncate">{manga.author} · {manga.category}</p>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = slug ? categorySlugMap[slug] || '热血' : '热血';
  const [activeCategory, setActiveCategory] = useState(categoryName);

  useEffect(() => {
    if (slug && categorySlugMap[slug]) {
      setActiveCategory(categorySlugMap[slug]);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const filteredManga = mangaList.filter((m) => m.category === activeCategory);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${activeCategory}漫画 - 木瓜漫画`,
            description: `木瓜漫画${activeCategory}漫画分类，为您精选优质${activeCategory}漫画作品，免费在线阅读。`,
            url: `https://www.muguamanhua.com/category/${slug}`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.muguamanhua.com/' },
              { '@type': 'ListItem', position: 2, name: `${activeCategory}漫画`, item: `https://www.muguamanhua.com/category/${slug}` },
            ],
          }),
        }}
      />

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: `${activeCategory}漫画` },
        ]}
      />

      <section className="py-8 md:py-12 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
            {activeCategory}漫画
          </h1>
          <p className="text-sm text-foreground-500 mb-8">
            精选优质{activeCategory}漫画作品，免费在线畅读
          </p>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.name
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className={cat.icon}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Manga Grid */}
          {filteredManga.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {filteredManga.map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center mx-auto text-3xl text-foreground-300">
                <i className="ri-emotion-sad-line"></i>
              </span>
              <p className="text-foreground-500 mt-4">该分类暂无漫画，敬请期待</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}